import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// One shared fetch of the current user's homecare rooms (role-appropriate endpoint),
// returning a map keyed by reservation_id -> { room_id, unread, status, service_status }.
// Reuses the SAME endpoints the sidebar uses; ONE call per page (+ light interval),
// never a separate poll per card. Client magic-tokens are never involved here.
export function useHomecareRooms(pollMs = 15000) {
  const { user } = useAuth();
  const role = user?.role;
  const isStaff = role === "petugas";
  const isAdmin = role === "admin" || role === "super_admin";
  const [byReservation, setByReservation] = useState({});
  const timer = useRef(null);

  const refresh = useCallback(() => {
    if (!isStaff && !isAdmin) return;
    const url = isStaff ? "/homecare/rooms/mine" : "/homecare/rooms?status=open";
    api.get(url).then((r) => {
      const map = {};
      (r.data?.rooms || []).forEach((room) => {
        // Grouped multi-service rooms map EVERY grouped child reservation to the SAME room.
        const ids = room.reservation_ids && room.reservation_ids.length ? room.reservation_ids : (room.reservation_id ? [room.reservation_id] : []);
        ids.forEach((rid) => {
          map[String(rid)] = {
            room_id: room.id,
            unread: room.unread || 0,
            status: room.status,
            service_status: room.service_status,
          };
        });
      });
      setByReservation(map);
    }).catch(() => {});
  }, [isStaff, isAdmin]);

  useEffect(() => {
    refresh();
    if (pollMs > 0) {
      timer.current = setInterval(() => { if (!document.hidden) refresh(); }, pollMs);
      return () => clearInterval(timer.current);
    }
  }, [refresh, pollMs]);

  return { byReservation, refresh, role };
}
